/**
 * Tasks API Service
 *
 * This service provides methods for interacting with task queue
 * backend endpoints including status, result, cancellation, and listing.
 */

import apiClient from './apiClient';
import type {
  TaskStatusResponse,
  TaskResultResponse,
  TaskListResponse,
  TaskListQuery,
} from '@/types/tasks';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build query string from parameters
 */
const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ============================================================================
// Tasks API
// ============================================================================

/**
 * Get current task status and progress.
 *
 * @param taskId - The Dramatiq message ID
 * @returns Task status information
 */
export const getTaskStatus = async (
  taskId: string
): Promise<TaskStatusResponse> => {
  return apiClient.get<TaskStatusResponse>(`/tasks/${taskId}/status`);
};

/**
 * Get task result after completion.
 *
 * @param taskId - The Dramatiq message ID
 * @returns Task result data
 */
export const getTaskResult = async (
  taskId: string
): Promise<TaskResultResponse> => {
  return apiClient.get<TaskResultResponse>(`/tasks/${taskId}/result`);
};

/**
 * Cancel a running task.
 *
 * @param taskId - The Dramatiq message ID
 * @returns void (204 No Content)
 */
export const cancelTask = async (
  taskId: string
): Promise<void> => {
  return apiClient.delete<void>(`/tasks/${taskId}`);
};

/**
 * List all tasks for the current user.
 *
 * @param query - Query parameters for filtering and pagination
 * @returns Paginated list of tasks
 */
export const listTasks = async (
  query: TaskListQuery = {}
): Promise<TaskListResponse> => {
  const queryString = buildQueryString({
    skip: query.skip,
    limit: query.limit,
    status_filter: query.status_filter,
  });
  return apiClient.get<TaskListResponse>(`/tasks${queryString}`);
};

// ============================================================================
// Export all API functions
// ============================================================================

const tasksApi = {
  getTaskStatus,
  getTaskResult,
  cancelTask,
  listTasks,
};

export default tasksApi;
